// Watching a label batch run.
//
// Printing 36 labels is 36 round trips to five carrier APIs. A minute is normal;
// several minutes happens when one carrier is slow. What this replaces was a
// blank window reading "Generating labels..." — so a slow batch and a dead one
// looked exactly the same, and the reasonable response (press Print again) bought
// every parcel a second time. On 2026-08-28 that was 106 labels for 36 orders.
//
// This panel is the middle step: nothing is hidden, every order shows what
// happened to it as it happens, and the labels that ARE ready can be printed
// without waiting for the ones that are not — so a stuck carrier no longer holds
// up the parcels that are good to go.
//
// Stored-first (2026-08-31): Print labels prints each order's STORED label
// where a live one exists — bought by the pre-buy sweep or an earlier run —
// and buys from the carrier only for orders without one. Pressing Print twice
// can no longer buy the same parcel twice. Re-sending one existing label
// unchanged stays on its own button: open the order and use Reprint.
//                                                        (Claude, 2026-08-31)
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authFetch, API } from './api';
import { deliverLabel } from './labelPrint';

interface OrderRow {
  order_id: string;
  /** Country order number — what the packer recognises; order_id is the WMS key. */
  order_number?: string;
  status: 'pending' | 'working' | 'done' | 'failed';
  tracking?: string;
  carrier?: string;
  error?: string;
  warning?: string;
  // Stored-first: true when the order's existing label was printed from
  // storage — no carrier was called and no new shipment was bought.
  reused?: boolean;
  // A label is spent by dispatch: the order was archived and its stored label
  // left with the parcel, so this run bought a NEW one (a re-ship).
  after_dispatch?: boolean;
}
interface Job {
  job_id: string; status: 'running' | 'finished' | 'error';
  total: number; done: number; failed: number; pending: number;
  error?: string; orders: OrderRow[];
}

export default function LabelRunPanel({ jobId, zebra, onClose, onPrinted }: {
  jobId: string;
  zebra: any;
  onClose: () => void;
  onPrinted?: () => void;
}) {
  const [job, setJob] = useState<Job | null>(null);
  const [printing, setPrinting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [printedCount, setPrintedCount] = useState(0);
  const timer = useRef<any>(null);

  const poll = useCallback(async () => {
    const r = await authFetch(`${API}/orders/print-labels/status/${jobId}`);
    if (!r.ok) {
      const d = await r.json().catch(() => ({}));
      setNotice(d.detail || 'This label run could not be found.');
      return true;                        // stop polling
    }
    const d: Job = await r.json();
    setJob(d);
    return d.status !== 'running';
  }, [jobId]);

  useEffect(() => {
    let stopped = false;
    const tick = async () => {
      const finished = await poll();
      if (!stopped && !finished) timer.current = setTimeout(tick, 1000);
    };
    tick();
    return () => { stopped = true; clearTimeout(timer.current); };
  }, [poll]);

  const printReady = async () => {
    if (printing || !job || job.done === 0) return;
    setPrinting(true);
    // Opened here, inside the click, so the browser treats it as a trusted gesture
    const win = window.open('', '_blank');
    if (win) win.document.write('<p style="font-family:sans-serif;padding:2rem">Preparing labels…</p>');
    try {
      const r = await authFetch(`${API}/orders/print-labels/output/${jobId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format: zebra ? 'zpl' : 'pdf' }),
      });
      const d = await r.json();
      if (!r.ok) { if (win) win.close(); setNotice(d.detail || 'Could not build the labels.'); return; }
      await deliverLabel(win, d, zebra);
      setPrintedCount(d.count || 0);
      setNotice(null);
      onPrinted?.();
    } catch {
      if (win) win.close();
      setNotice('Something went wrong sending the labels to the printer.');
    } finally { setPrinting(false); }
  };

  const pct = job && job.total ? Math.round(((job.done + job.failed) / job.total) * 100) : 0;
  const running = job?.status === 'running';
  const stuck = job?.orders.filter(o => o.status === 'working') || [];
  const failed = job?.orders.filter(o => o.status === 'failed') || [];
  const allFailed = !!job && !running && job.done === 0 && job.failed > 0;

  // Closing does not stop the run — the carriers have already been asked and the
  // labels are being bought either way. What closing DOES lose is the only place
  // those labels can be printed as one stack, so it is worth a word first.
  const close = () => {
    if ((running || (job && job.done > printedCount)) && printedCount === 0) {
      const msg = running
        ? 'Labels are still being bought. Closing does not stop that — the labels will be on their orders, '
          + 'but you will have to reprint them one at a time. Close anyway?'
        : `${job?.done} label(s) have not been printed yet. Closing means reprinting them one at a time. Close anyway?`;
      if (!window.confirm(msg)) return;
    }
    onClose();
  };

  const dot = (s: OrderRow['status']) =>
    s === 'done' ? 'bg-green-500'
      : s === 'failed' ? 'bg-red-500'
      : s === 'working' ? 'bg-blue-500 animate-pulse'
      : 'bg-gray-700';

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-6 z-50">
      <div className="bg-[#1a1d27] border border-gray-800 rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-gray-800">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-white">
                {running ? 'Preparing labels'
                  : allFailed ? 'No labels were created'
                  : 'Labels ready'}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {job
                  ? <>
                      {job.done} of {job.total} done
                      {job.failed > 0 && <span className="text-red-400"> · {job.failed} failed</span>}
                    </>
                  : 'Starting…'}
              </p>
            </div>
            <button onClick={close} className="text-gray-500 hover:text-white text-lg leading-none">✕</button>
          </div>

          <div className="mt-3 h-2 rounded-full bg-gray-800 overflow-hidden">
            <div className="h-full bg-blue-500 transition-all duration-300" style={{ width: `${pct}%` }} />
          </div>

          {running && stuck.length > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              Waiting on {stuck.map(o => o.order_number || o.order_id).join(', ')} — a carrier is allowed
              up to 30 seconds to answer (Packeta up to two minutes).
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5 pt-3">
          {notice && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-sm text-gray-200">
              {notice}
            </div>
          )}
          {job?.status === 'error' && (
            <div className="mb-3 px-3 py-2 rounded-lg bg-red-950/40 border border-red-900 text-sm text-red-300">
              The run stopped: {job.error}. The labels it did buy are on their orders — print them from here, or reprint from the order later.
            </div>
          )}
          <table className="w-full text-sm">
            <tbody>
              {job?.orders.map(o => (
                <tr key={o.order_id} className="border-b border-gray-800/50">
                  <td className="py-1.5 w-6">
                    <span className={`inline-block w-2 h-2 rounded-full ${dot(o.status)}`} />
                  </td>
                  <td className="py-1.5 text-gray-200 font-medium whitespace-nowrap pr-3">{o.order_number || o.order_id}</td>
                  <td className="py-1.5 text-gray-500 whitespace-nowrap pr-3">{o.carrier || ''}</td>
                  <td className="py-1.5 text-gray-400 w-full">
                    {o.status === 'done' && (
                      <span className="text-gray-400">
                        {o.tracking}
                        <span className={`ml-2 text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                          o.reused ? 'bg-gray-800 text-gray-500'
                          : o.after_dispatch ? 'bg-amber-900/50 text-amber-300' : 'bg-blue-900/50 text-blue-400'
                        }`}>
                          {o.reused ? 'printed from stock' : o.after_dispatch ? 'new label — order was dispatched' : 'bought now'}
                        </span>
                        {o.warning && <span className="text-amber-400"> · {o.warning}</span>}
                      </span>
                    )}
                    {o.status === 'failed' && <span className="text-red-400">{o.error}</span>}
                    {o.status === 'working' && <span className="text-blue-400">asking the carrier…</span>}
                    {o.status === 'pending' && <span className="text-gray-700">waiting</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-5 border-t border-gray-800 flex items-center justify-between gap-3">
          <div className="text-xs text-gray-500">
            {printedCount > 0
              ? `${printedCount} label${printedCount !== 1 ? 's' : ''} sent to print. Stored labels were reused; only orders without one, or already dispatched, bought a new shipment.`
              : failed.length > 0
                ? 'Print what is ready; the failed ones keep their place in the tab and can be printed later.'
                : 'You can print as soon as the ones you need are done.'}
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={close}
              className="px-3 py-2 text-sm rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300">
              Close
            </button>
            <button onClick={printReady} disabled={printing || !job || job.done === 0}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-medium disabled:opacity-40">
              {printing ? 'Preparing…'
                : running ? `Print the ${job?.done || 0} that are ready`
                : `Print ${job?.done || 0} label${(job?.done || 0) !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
